import React, { useState } from 'react';
import { useFamily } from '../../context/FamilyContext';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { formatCurrency, getCategoryIcon } from '../../utils/expenseUtils';
import { formatDate } from '@coparent/shared/src/utils/dateUtils';
import { settleExpense, deleteExpense, uploadReceipt, getExpenseReceipts } from '@coparent/shared/src/services/expenseService';
import { SETTLEMENT_METHODS, EXPENSE_CATEGORIES, MAX_FILE_SIZE_BYTES } from '@coparent/shared/src/utils/constants';
import ChildDots from '../common/ChildDots';
import DiscussButton from '../common/DiscussButton';
import { HiCheck, HiTrash, HiPencil, HiChevronDown, HiChevronUp, HiClock, HiPaperClip } from 'react-icons/hi';
import { HiArrowUpTray } from 'react-icons/hi2';
import toast from 'react-hot-toast';

const getParticipantName = (id, family) => {
  if (family?.parentProfiles?.[id]) return family.parentProfiles[id].displayName;
  if (family?.householdMembers?.[id]) return family.householdMembers[id].displayName;
  return null;
};

const formatAuditValue = (field, value, family, childrenMap) => {
  if (value === null || value === undefined || value === '') return '—';
  if (field === 'Amount') return formatCurrency(value);
  if (field === 'Split ratio') return `${value}%`;
  if (field === 'Date') {
    return formatDate(value);
  }
  if (field === 'Paid by') {
    const name = getParticipantName(value, family);
    if (name) return name;
  }
  if (field === 'Category') {
    const cat = EXPENSE_CATEGORIES.find(c => c.value === value);
    return cat ? cat.label : value;
  }
  if (field === 'Child' && Array.isArray(value)) {
    if (!value.length) return 'All children';
    return value
      .map(id => {
        const child = childrenMap?.[id];
        return child?.name || child?.firstName || id;
      })
      .join(', ');
  }
  return String(value);
};

const ExpenseList = ({ expenses, onEdit }) => {
  const { familyId, family, childrenMap, getParticipantProfile } = useFamily();
  const { currentUser } = useAuth();
  const { canSettle, canDelete } = usePermissions();
  const [expandedAudit, setExpandedAudit] = useState(null);
  const [pendingSettle, setPendingSettle] = useState(null);
  const [isSettling, setIsSettling] = useState(false);
  const [uploadingExpenseId, setUploadingExpenseId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = React.useRef(null);
  const uploadTargetRef = React.useRef(null);

  const RECEIPT_TYPES = new Set(['image/jpeg', 'image/png', 'image/heic', 'image/webp', 'application/pdf']);

  const handleReceiptUpload = (expenseId) => {
    uploadTargetRef.current = { expenseId };
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !uploadTargetRef.current) return;

    if (!RECEIPT_TYPES.has(file.type)) {
      toast.error('Only images and PDFs are accepted');
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error('File must be under 10MB');
      return;
    }

    const { expenseId } = uploadTargetRef.current;
    setUploadingExpenseId(expenseId);
    setUploadProgress(0);
    try {
      await uploadReceipt(familyId, expenseId, file, setUploadProgress);
      toast.success('Receipt uploaded!');
    } catch {
      toast.error('Failed to upload receipt');
    }
    setUploadingExpenseId(null);
    uploadTargetRef.current = null;
  };

  const resolveToParent = (id) => {
    if (family.parentIds.includes(id)) return id;
    const member = family.householdMembers?.[id];
    if (member?.householdOf && family.parentIds.includes(member.householdOf)) {
      return member.householdOf;
    }
    return id;
  };

  const confirmSettle = async () => {
    if (!pendingSettle) return;
    const { expense, method } = pendingSettle;
    const resolvedPayer = resolveToParent(expense.paidBy);
    const otherParent = family.parentIds.find(id => id !== resolvedPayer);
    setIsSettling(true);
    try {
      await settleExpense(familyId, expense.id, method, {
        paidBy: otherParent,
        paidTo: expense.paidBy,
        amount: expense.owedAmount,
        createdBy: currentUser.uid
      });
      toast.success('Marked as settled');
      setPendingSettle(null);
    } catch (error) {
      toast.error('Failed to settle expense');
    } finally {
      setIsSettling(false);
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await deleteExpense(familyId, expenseId);
      toast.success('Expense deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const toggleAudit = (expenseId) => {
    setExpandedAudit(prev => prev === expenseId ? null : expenseId);
  };

  if (!expenses.length) {
    return (
      <div className="text-center py-16 text-ink-300">
        <p className="text-base font-semibold">No expenses yet</p>
        <p className="text-sm mt-1">Add your first shared expense to start tracking.</p>
      </div>
    );
  }

  const settleMethodLabel = pendingSettle
    ? SETTLEMENT_METHODS.find(m => m.value === pendingSettle.method)?.label
    : '';

  return (
    <div className="space-y-3">
      {/* Settle confirmation modal */}
      {pendingSettle && (
        <div className="modal-overlay" onClick={() => !isSettling && setPendingSettle(null)}>
          <div className="modal-content max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold font-display text-ink-900 mb-4">Confirm settlement</h3>
            <div className="bg-surface-50 rounded-xl p-4 mb-5 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-ink-500">Expense</span>
                <span className="text-sm font-semibold text-ink-900">{pendingSettle.expense.description}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-ink-500">Amount owed</span>
                <span className="text-sm font-bold text-ink-900">{formatCurrency(pendingSettle.expense.owedAmount)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-ink-500">Method</span>
                <span className="text-sm font-semibold text-ink-900">{settleMethodLabel}</span>
              </div>
            </div>
            <p className="text-xs text-ink-400 mb-5">This will mark the expense as settled. This action cannot be undone.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setPendingSettle(null)}
                disabled={isSettling}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={confirmSettle}
                disabled={isSettling}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                <HiCheck className="text-base" />
                {isSettling ? 'Settling...' : 'Settle'}
              </button>
            </div>
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/heic,image/webp,application/pdf"
        onChange={handleFileSelected}
        className="hidden"
      />

      {expenses.map(expense => {
        const paidByProfile = getParticipantProfile(expense.paidBy);
        const hasHistory = expense.editHistory?.length > 0;
        const isAuditOpen = expandedAudit === expense.id;

        return (
          <div
            key={expense.id}
            className={`card-hover p-4 ${
              expense.status === 'settled' ? 'bg-shared-50/30 border-shared-200/40' : ''
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <span className="text-2xl">{getCategoryIcon(expense.category)}</span>
                <div>
                  <div className="flex items-center gap-1.5">
                    <p className="font-semibold text-ink-900 text-sm">{expense.description}</p>
                    {expense.status === 'pending' && uploadingExpenseId !== expense.id && (
                      <button
                        onClick={() => handleReceiptUpload(expense.id)}
                        className="text-canopy-600 hover:text-canopy-700 transition-colors"
                        title="Attach receipt"
                      >
                        <HiArrowUpTray className="text-lg" />
                      </button>
                    )}
                    {(() => {
                      const receipts = getExpenseReceipts(expense);
                      if (!receipts.length) return null;
                      return (
                        <button
                          onClick={() => receipts.forEach(r => window.open(r.url, '_blank'))}
                          className="inline-flex items-center gap-0.5 text-canopy-600 hover:text-canopy-700 transition-colors"
                          title={`${receipts.length} receipt${receipts.length > 1 ? 's' : ''}`}
                        >
                          <HiPaperClip className="text-base" />
                          {receipts.length > 1 && (
                            <span className="text-[0.625rem] font-bold">{receipts.length}</span>
                          )}
                        </button>
                      );
                    })()}
                  </div>
                  <p className="text-xs text-ink-400 mt-0.5">
                    {formatDate(expense.date)} &middot; Paid by{' '}
                    <span className="font-semibold" style={{ color: paidByProfile?.color }}>
                      {paidByProfile?.displayName}
                    </span>
                  </p>
                  {expense.note && (
                    <p className="text-xs text-ink-300 mt-1">{expense.note}</p>
                  )}
                  {expense.childIds?.length > 0 && (
                    <div className="mt-1">
                      <ChildDots childIds={expense.childIds} childrenMap={childrenMap} showNames />
                    </div>
                  )}
                  {expense.autoCreated && (
                    <p className="text-[0.625rem] text-ink-300 mt-1 italic">
                      {expense.recurringExpenseId ? 'Auto-created from recurring expense' : 'Auto-created from activity'}
                    </p>
                  )}
                  {uploadingExpenseId === expense.id && (
                    <div className="mt-1.5 w-32">
                      <div className="h-1 bg-surface-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-canopy-500 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="text-right flex items-start gap-2">
                <div>
                  <p className="font-bold text-ink-900">{formatCurrency(expense.amount)}</p>
                  <p className="text-[0.6875rem] text-ink-400">
                    {formatCurrency(expense.owedAmount)} owed
                  </p>
                </div>
                <DiscussButton
                  type="expense"
                  id={expense.id}
                  label={`${expense.description} · ${formatCurrency(expense.amount)}`}
                  childIds={expense.childIds || []}
                />
                {expense.status === 'pending' && (
                  <button
                    onClick={() => onEdit(expense)}
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-ink-300 hover:text-ink-600 hover:bg-surface-100 transition-colors"
                    title="Edit expense"
                  >
                    <HiPencil className="text-sm" />
                  </button>
                )}
              </div>
            </div>

            {expense.status === 'pending' && (canSettle || canDelete) && (
              <div className="mt-3 flex items-center gap-2 pt-3 border-t border-surface-100">
                {canSettle && (
                  <>
                    <span className="text-[0.6875rem] text-ink-400 mr-auto font-medium">Settle via:</span>
                    {SETTLEMENT_METHODS.map(method => (
                      <button
                        key={method.value}
                        onClick={() => setPendingSettle({ expense, method: method.value })}
                        className="chip-inactive text-[0.6875rem] !bg-shared-50 !text-shared-700 hover:!bg-shared-100"
                      >
                        {method.label}
                      </button>
                    ))}
                  </>
                )}
                {canDelete && (
                  <button
                    onClick={() => handleDelete(expense.id)}
                    className="ml-1 w-7 h-7 rounded-lg flex items-center justify-center text-ink-300 hover:text-accent-600 hover:bg-accent-50 transition-colors"
                  >
                    <HiTrash className="text-sm" />
                  </button>
                )}
              </div>
            )}

            {expense.status === 'settled' && (
              <div className="mt-3 flex items-center gap-2 pt-3 border-t border-shared-100/60">
                <HiCheck className="text-shared-500" />
                <span className="text-xs text-shared-600 font-medium">
                  Settled via {expense.settledMethod} &middot; {formatDate(expense.settledAt)}
                </span>
              </div>
            )}

            {/* Audit trail toggle */}
            {hasHistory && (
              <div className="mt-2">
                <button
                  onClick={() => toggleAudit(expense.id)}
                  className="flex items-center gap-1.5 text-[0.6875rem] text-ink-400 hover:text-ink-600 font-medium transition-colors"
                >
                  <HiClock className="text-xs" />
                  {expense.editHistory.length} edit{expense.editHistory.length > 1 ? 's' : ''}
                  {isAuditOpen ? <HiChevronUp className="text-xs" /> : <HiChevronDown className="text-xs" />}
                </button>

                {isAuditOpen && (
                  <div className="mt-2 space-y-2.5 pl-1">
                    {[...expense.editHistory].reverse().map((entry, idx) => (
                      <div key={idx} className="border-l-2 border-surface-200 pl-3 py-1">
                        <p className="text-[0.6875rem] text-ink-500 font-semibold">
                          {entry.editedByName} &middot;{' '}
                          <span className="font-normal text-ink-400">
                            {formatDate(entry.editedAt, 'MMM d, yyyy h:mm a')}
                          </span>
                        </p>
                        <div className="mt-1 space-y-0.5">
                          {entry.changes.map((change, ci) => (
                            <p key={ci} className="text-[0.625rem] text-ink-400">
                              <span className="font-semibold text-ink-500">{change.field}:</span>{' '}
                              <span className="line-through text-ink-300">{formatAuditValue(change.field, change.from, family, childrenMap)}</span>
                              {' → '}
                              <span className="text-ink-600">{formatAuditValue(change.field, change.to, family, childrenMap)}</span>
                            </p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ExpenseList;
